import { type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Lock, Pencil } from 'lucide-react';
import {
  RUM_TYPE_LABELS,
  canEditRum,
  toDisplayScore,
  type RumTastingsView,
} from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { RumPhotoField } from '@/shared/rum/RumPhotoField';
import { isHttpError } from '@/shared/http/http-client';
import { Avatar, Badge, Card, EmptyState, Kicker, Muted, Skeleton, Tag } from '@/shared/ui';
import { formatShortDate } from '../session/format';
import { RumComments } from './RumComments';
import { RumLinks } from './RumLinks';
import styles from './rum.module.scss';

/**
 * Écran 03 — fiche rhum.
 *
 * Les notes du cercle sont chargées séparément : l'API répond 403 tant que le membre n'a
 * pas validé la sienne (règle anti-biais). Ce 403 n'est pas une erreur à afficher mais un
 * état normal de l'écran, qu'on traduit par le bandeau « validez d'abord la vôtre ».
 */
export function RumScreen(): ReactNode {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get('sessionId');
  const api = useApi();
  const { member } = useApp();

  const rum = useAsync(() => api.rums.detail(id), [id]);
  // Le statut de la séance décide de l'apparition des liens « Noter » : la fiche est
  // consultable en tout temps, la note ne s'ouvre qu'une fois la séance lancée.
  const session = useAsync(
    () => (sessionId ? api.sessions.byId(sessionId) : Promise.resolve(null)),
    [sessionId],
  );
  const tastings = useAsync(
    () =>
      sessionId
        ? api.rums.tastings(id, sessionId).catch((cause: unknown) => {
            // 403 = anti-biais. C'est attendu, pas une panne.
            if (isHttpError(cause) && cause.status === 403) return null;
            throw cause;
          })
        : Promise.resolve(null),
    [id, sessionId],
  );

  if (rum.loading) return <Skeleton />;
  if (rum.error || !rum.data) return <EmptyState>{rum.error ?? 'Fiche introuvable.'}</EmptyState>;

  const data = rum.data;
  const notable = sessionId !== null && session.data?.status === 'OPEN';

  /*
   * Le droit de corriger la fiche, calculé une fois.
   *
   * Le prédicat vient de `@cercle/shared`, celui-là même dont l'API se sert pour refuser :
   * c'est ce qui garantit que le bouton apparaît exactement quand l'enregistrement passera.
   * Une fiche masquée n'est jamais modifiable — la reprendre reviendrait à la regarder, et
   * le mode aveugle tomberait par la porte de service.
   */
  const editable =
    !data.isMasked &&
    member != null &&
    canEditRum(member.role, member.id, data.createdBy?.id ?? null);

  return (
    <article className={styles.page}>
      {/*
        Pas de grande photo en tête.

        L'écran en portait une, et la section « Photo » plus bas en montrait une seconde :
        la même bouteille deux fois sur une page qu'on parcourt au pouce. C'est la section
        « Photo » qui reste, parce qu'elle est aussi le moyen de la changer — la vignette
        seule n'aurait servi qu'à décorer.
      */}
      <section className={styles.hero}>
        <header className={styles.identity}>
          {data.lastTastedAt ? (
            <Kicker>Dégusté le {formatShortDate(data.lastTastedAt)}</Kicker>
          ) : (
            <Kicker>À déguster</Kicker>
          )}
          <h1>{data.name}</h1>
          {data.isClubFavourite ? (
            <span className={styles.favourite}>
              <Badge>Coup de cœur</Badge>
            </span>
          ) : null}
          <Muted>
            {data.isMasked
              ? 'Fiche masquée par l’administrateur'
              : [data.distillery, data.country].filter(Boolean).join(' · ')}
            {data.broughtBy ? ` · apporté par ${data.broughtBy.pseudo}` : ''}
          </Muted>

          <div className={styles.scores}>
            <span className={styles.overall}>
              <strong>{data.averageScore === null ? '—' : toDisplayScore(data.averageScore)}</strong>
              <span>/ 10{data.tastingCount > 0 ? ` · ${data.tastingCount} notes` : ''}</span>
            </span>
            <dl className={styles.criteria}>
              <Criterion label="Nez" value={data.averageNose} />
              <Criterion label="Bouche" value={data.averagePalate} />
              <Criterion label="Finale" value={data.averageFinish} />
            </dl>
          </div>

          <div className={styles.actions}>
            {notable ? (
              <Link className={styles.cta} to={`/rhums/${data.id}/noter?sessionId=${sessionId}`}>
                Noter ce rhum
              </Link>
            ) : null}
            {editable ? (
              <Link className={styles.edit} to={`/rhums/${data.id}/modifier`}>
                <Pencil size={16} strokeWidth={1.75} aria-hidden="true" />
                Modifier la fiche
              </Link>
            ) : null}
          </div>
        </header>
      </section>

      {!data.isMasked ? (
        <section>
          <h2 className={styles.sectionTitle}>Caractéristiques</h2>
          <dl className={styles.specs}>
            <Spec label="Distillerie" value={data.distillery} />
            <Spec label="Pays" value={data.country} />
            <Spec label="Type" value={data.rumType ? RUM_TYPE_LABELS[data.rumType] : null} />
            <Spec label="Style" value={data.style} />
            <Spec label="Degré" value={data.abv === null ? null : `${data.abv} %`} />
            <Spec label="Âge" value={data.age === null ? null : `${data.age} ans`} />
          </dl>
        </section>
      ) : null}

      {/*
        La photo est visible de tous, mais ne se reprend que par qui a le droit de corriger
        la fiche. Elle était offerte à tout le monde, et l'API répondait 403 au bouilleur qui
        essayait sur la fiche d'un autre.

        Sur un rhum masqué, la section disparaît entièrement : la photo est de toute façon
        retirée de la réponse, et proposer de la reprendre reviendrait à la regarder.
      */}
      {!data.isMasked ? (
        <section>
          <h2 className={styles.sectionTitle}>Photo</h2>
          <RumPhotoField
            rumId={data.id}
            photoUrl={data.photoUrl}
            editable={editable}
            onChange={() => rum.reload()}
          />
        </section>
      ) : null}

      {data.topAromaTags.length > 0 ? (
        <section>
          <h2 className={styles.sectionTitle}>Descripteurs du cercle</h2>
          <div className={styles.tags}>
            {data.topAromaTags.map(({ tag, count }) => (
              <Tag key={tag} gold>
                {tag}
                {count > 1 ? ` · ${count}` : ''}
              </Tag>
            ))}
          </div>
        </section>
      ) : null}

      {sessionId ? (
        <ClubTastings
          tastings={tastings.data}
          rumId={id}
          sessionId={sessionId}
          notable={notable}
        />
      ) : null}

      {/*
        Liens et discussion ferment la fiche, le fil en dernier — c'est ce qui s'allonge, et
        ce qu'on vient relire.

        Rien de tout cela sur un rhum masqué : « le test de Rhum Attitude sur le Caroni 1996 »
        nommerait la bouteille aussi sûrement que sa fiche, et un commentaire posté pendant la
        dégustation contournerait l'anti-biais par un champ de texte libre. L'API refuse déjà
        ces routes dans ce cas ; ne pas les monter évite d'afficher une erreur pour un refus
        qui est normal.
      */}
      {!data.isMasked ? (
        <>
          <RumLinks rumId={id} />
          <RumComments rumId={id} />
        </>
      ) : null}
    </article>
  );
}

function Criterion({ label, value }: { label: string; value: number | null }): ReactNode {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value === null ? '—' : toDisplayScore(value)}</dd>
    </div>
  );
}

function Spec({ label, value }: { label: string; value: string | null }): ReactNode {
  if (!value) return null;
  return (
    <div className={styles.spec}>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

/** `null` = l'API a refusé : le membre n'a pas encore validé sa note. */
function ClubTastings({
  tastings,
  rumId,
  sessionId,
  notable,
}: {
  tastings: RumTastingsView | null;
  rumId: string;
  sessionId: string;
  /** Séance lancée : sans cela le bandeau explique l'attente au lieu d'offrir un lien. */
  notable: boolean;
}): ReactNode {
  if (!tastings) {
    return (
      <Card className={styles.antiBias}>
        <Lock size={18} strokeWidth={2.75} aria-hidden="true" />
        <div>
          <strong>Les notes du cercle sont masquées</strong>
          <Muted>
            {notable
              ? 'Elles apparaîtront dès que vous aurez validé la vôtre — c’est la règle du cercle.'
              : 'Elles apparaîtront dès que la séance sera lancée et que vous aurez validé la vôtre.'}
          </Muted>
        </div>
        {notable ? <Link to={`/rhums/${rumId}/noter?sessionId=${sessionId}`}>Noter</Link> : null}
      </Card>
    );
  }

  return (
    <section>
      <h2 className={styles.sectionTitle}>
        Notes du cercle <Muted>· moyenne {tastings.average === null ? '—' : toDisplayScore(tastings.average)}</Muted>
      </h2>
      <ul className={styles.comments}>
        {tastings.tastings.map((tasting) => (
          <li key={tasting.id} className={styles.comment}>
            <Avatar pseudo={tasting.member.pseudo} url={tasting.member.avatarUrl} />
            <div>
              <strong>
                {tasting.member.pseudo} · {toDisplayScore(tasting.overall)}
              </strong>
              {tasting.comment ? <p>« {tasting.comment} »</p> : null}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
